import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

/** Days kept when a stored audit-retention value is missing or not a number. Matches the seeded config row. */
export const DEFAULT_AUDIT_RETENTION_DAYS = 365;

export const AUDIT_RETENTION_SEED = {
  api_audit: DEFAULT_AUDIT_RETENTION_DAYS,
  node_definition_history: DEFAULT_AUDIT_RETENTION_DAYS,
} as const;

const DAY_FIELDS = ['api_audit', 'node_definition_history'] as const;

type AuditDayField = (typeof DAY_FIELDS)[number];

export type AuditRetentionValue = {
  api_audit: number;
  node_definition_history: number;
};

/**
 * @param value - Candidate day count from the form or a stored row.
 * @returns The count when it is a finite number >= 0, otherwise undefined.
 */
export const parseAuditRetentionDays = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed >= 0) {
      return parsed;
    }
  }
  return undefined;
};

/**
 * @param value - Stored or in-progress audit-retention form value.
 * @returns True when both day counts are already finite and non-negative and nothing else is present.
 */
export const isAuditRetentionValue = (value: unknown): value is AuditRetentionValue => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }
  const record = value as Record<string, unknown>;
  if (Object.keys(record).length !== DAY_FIELDS.length) {
    return false;
  }
  return DAY_FIELDS.every((field) => typeof record[field] === 'number' && parseAuditRetentionDays(record[field]) === record[field]);
};

const readField = (formData: unknown, field: AuditDayField): unknown => {
  if (typeof formData !== 'object' || formData === null) {
    return undefined;
  }
  return (formData as Record<string, unknown>)[field];
};

/**
 * @param formData - Whatever the shared form bag currently holds.
 * @returns The two day counts, using {@link DEFAULT_AUDIT_RETENTION_DAYS} when a field is unreadable.
 */
export const auditRetentionFromForm = (formData: unknown): AuditRetentionValue => ({
  api_audit: parseAuditRetentionDays(readField(formData, 'api_audit')) ?? DEFAULT_AUDIT_RETENTION_DAYS,
  node_definition_history: parseAuditRetentionDays(readField(formData, 'node_definition_history')) ?? DEFAULT_AUDIT_RETENTION_DAYS,
});

const displayDay = (value: unknown): string => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === 'string') {
    return value;
  }
  return '';
};

/**
 * @param raw - Text from a day input, or the display form of the field that was not just edited.
 * @returns The numeric count when the text is exactly that count, so a restored value matches the loaded row. Otherwise the text, so an in-progress edit stays on screen.
 */
const storedAuditDay = (raw: string): number | string => {
  const parsed = parseAuditRetentionDays(raw);
  if (parsed !== undefined && String(parsed) === raw.trim()) {
    return parsed;
  }
  return raw;
};

const AuditRetentionFields: React.FC<{
  formData: unknown;
  setFormData: (value: { api_audit: number | string; node_definition_history: number | string }) => void;
  setJsonValid: (valid: boolean) => void;
}> = ({ formData, setFormData, setJsonValid }) => {
  const apiAudit = readField(formData, 'api_audit');
  const nodeHistory = readField(formData, 'node_definition_history');
  const apiAuditValid = parseAuditRetentionDays(apiAudit) !== undefined;
  const nodeHistoryValid = parseAuditRetentionDays(nodeHistory) !== undefined;

  const updateDay = (field: AuditDayField, raw: string) => {
    const next = {
      api_audit: field === 'api_audit' ? storedAuditDay(raw) : storedAuditDay(displayDay(apiAudit)),
      node_definition_history: field === 'node_definition_history' ? storedAuditDay(raw) : storedAuditDay(displayDay(nodeHistory)),
    };
    setFormData(next);
    setJsonValid(parseAuditRetentionDays(next.api_audit) !== undefined && parseAuditRetentionDays(next.node_definition_history) !== undefined);
  };

  return (
    <>
      <Form.Group as={Row} className="mb-3" controlId="auditRetentionApiAudit">
        <Form.Label column sm="2">
          API audit (days)
        </Form.Label>
        <Col sm="10">
          <Form.Control
            type="number"
            min={0}
            name="api_audit"
            value={displayDay(apiAudit)}
            onChange={(event) => updateDay('api_audit', event.target.value)}
            isInvalid={!apiAuditValid}
          />
          {!apiAuditValid ? <Form.Text className="text-danger">API audit days must be a number that is zero or greater.</Form.Text> : null}
        </Col>
      </Form.Group>

      <Form.Group as={Row} className="mb-3" controlId="auditRetentionNodeHistory">
        <Form.Label column sm="2">
          Node definition history (days)
        </Form.Label>
        <Col sm="10">
          <Form.Control
            type="number"
            min={0}
            name="node_definition_history"
            value={displayDay(nodeHistory)}
            onChange={(event) => updateDay('node_definition_history', event.target.value)}
            isInvalid={!nodeHistoryValid}
          />
          {!nodeHistoryValid ? <Form.Text className="text-danger">Node definition history days must be a number that is zero or greater.</Form.Text> : null}
        </Col>
      </Form.Group>
    </>
  );
};

export default AuditRetentionFields;
